import { Injectable } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { FitApiClientService, FitList } from '@shared/fit-api-client.service';

import { GalleryModalComponent } from './gallery-modal/gallery-modal.component';
import { GalleryVideoCropComponent } from './gallery-video-crop/gallery-video-crop.component';
import { GalleryItem } from './gallery-item';
import { MarkerDimensions } from '@shared/components/area-marker/area-marker.component';

export { GalleryItem };

export interface GalleryUploadLimits {
    image_types: string[];
    video_types: string[];
    upload_limit_in_bytes: number;
    document_types: string[];
    image_upload_limit_in_bytes?: number;
    video_upload_limit_in_bytes?: number;
}

// var acceptedVideoMimeTypes = ['mp4', 'mpeg4', 'mpeg', 'mov'];
// var acceptedImageMimeTypes = ['jpeg', 'png'];

function formatBytes(bytes, decimals) {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

@Injectable()
export class GalleryService {
    constructor(private fitApi: FitApiClientService, private modalService: BsModalService) {
        void this.ensureUploadLimits();
    }

    public type: 'all' | 'image' | 'video' | 'document' = 'all';
    public uploadLimits: GalleryUploadLimits = {
        image_types: [],
        upload_limit_in_bytes: 0,
        video_types: [],
        document_types: []
    };

    public formattedBytes = '';

    private uploadLimitsPromise: Promise<void> | null = null;

    public search(query: string, page: number, order: string, type?: 'video' | 'image' | 'document') {
        return this.fitApi.get<FitList<GalleryItem>>('/admin/gallery', {
            search: query,
            type,
            page: page.toString(),
            order
        });
    }

    public searchAvatar(query: string, page: number, order: string, type?: 'video' | 'image') {
        return this.fitApi.get<FitList<GalleryItem>>('/admin/gallery/avatar', {
            search: query,
            type,
            page: page.toString(),
            order
        });
    }

    public formatBytes(bytes, decimals) {
        return formatBytes(bytes, decimals);
    }

    public getFormattedByteSize(decimals: number, type?: 'image' | 'video'): string {
        const limits = this.uploadLimits;
        let bytes = limits.upload_limit_in_bytes;

        if (type === 'image' && limits.image_upload_limit_in_bytes) {
            bytes = limits.image_upload_limit_in_bytes;
        } else if (type === 'video' && limits.video_upload_limit_in_bytes) {
            bytes = limits.video_upload_limit_in_bytes;
        }

        return formatBytes(bytes, decimals);
    }

    public getMediaFormatHint(type: 'image' | 'video', allowedTypes?: string[]): string {
        const types = allowedTypes ?? this.getAcceptableFileTypes(type) ?? [];
        if (!types.length) {
            return '';
        }

        const typeLabel = this.formatAcceptableFileTypes(types);
        const sizeLabel = this.getFormattedByteSize(0, type).replace(' ', '');

        return `${typeLabel} · max ${sizeLabel}`;
    }

    public fixVideo(id: number) {
        return this.fitApi.post<any>('/admin/fix-video', { id });
    }

    public selectFromGallery(type: 'all' | 'image' | 'video' | 'document', source: 'gallery' | 'avatar'): Promise<GalleryItem> {
        const promise = new Promise<GalleryItem>((resolve, reject) => {
            const initialState = {
                onClose: (item: GalleryItem) => {
                    resolve(item);
                },
                type,
                source
            };
            this.bsModalRef = this.modalService.show(GalleryModalComponent,
                { initialState, backdrop: false, ignoreBackdropClick: true, class: 'gallery-modal' });
        });
        return promise;
    }

    public openCropGalleryItemWindow(item: GalleryItem): Promise<MarkerDimensions> {
        const promise = new Promise<MarkerDimensions>((resolve, reject) => {
            const initialState = {
                onClose: (dimensions: MarkerDimensions) => {
                    resolve(dimensions);
                },
                item
            };
            this.bsModalRef = this.modalService.show(GalleryVideoCropComponent,
                { initialState, backdrop: false, ignoreBackdropClick: true, class: 'gallery-video-crop-modal' });
        })
        return promise;
    }

    public cropGalleryVideoItem(item: GalleryItem, dimensions: MarkerDimensions) {
        return this.fitApi.post<GalleryItem>(`/admin/crop-video`, {
            id: item.id,
            width: dimensions.width,
            height: dimensions.height,
            x: dimensions.left,
            y: dimensions.top
        });
    }

    public delete(id: number) {
        return this.fitApi.delete<any>(`/admin/gallery/${id}`);
    }


    public update(item: GalleryItem) {
        return this.fitApi.put<GalleryItem>(`/admin/gallery/${item.id}`, item);
    }

    public upload(fileName: string, file: File, isUserAvatar: boolean, type: 'image' | 'video' | 'document' | 'all') {
        const form = new FormData();
        form.append('url', file);
        form.append('name', fileName);
        form.append('type', type);
        form.append('is_user_avatar', isUserAvatar ? 'true' : 'false');
        return this.fitApi.uploadFileToGallery<GalleryItem>(form);
    }

    public uploadFromProfile(fileName: string, file: File | Blob, isUserAvatar: boolean) {
        const form = new FormData();
        form.append('url', file);
        form.append('name', fileName);
        form.append('is_user_avatar', isUserAvatar ? 'true' : 'false');
        return this.fitApi.uploadFileFromProfile<GalleryItem>(form);
    }

    public getUploadLimits() {
        void this.ensureUploadLimits();
    }

    public ensureUploadLimits(): Promise<void> {
        if (this.uploadLimitsPromise) {
            return this.uploadLimitsPromise;
        }

        this.uploadLimitsPromise = this.fitApi.get<GalleryUploadLimits>('/admin/gallery/upload-limits').then(res => {
            this.uploadLimits = res.data;
            this.formattedBytes = this.formatBytes(this.uploadLimits.upload_limit_in_bytes, 2);
        });

        return this.uploadLimitsPromise;
    }

    public getImageById(id: number){
        return this.fitApi.get<GalleryItem>(`/admin/gallery/${id}`);
    }

    public isValidFileType(file: File, acceptedType: string) {
        const types = this.getAcceptableFileTypes(acceptedType) ?? [];
        const mime = (file.type || '').toLowerCase();
        const fileName = (file.name || '').toLowerCase();
        const extension = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.') + 1) : '';

        // Chrome on Linux often reports an empty MIME type for PDFs.
        if (!types.length) {
            return acceptedType !== 'document' || extension === 'pdf';
        }

        return types.some(type => {
            const token = type.toLowerCase().replace(/^\./, '');
            if (mime && mime.indexOf(token) > -1) {
                return true;
            }
            const tokenExt = token.includes('/') ? token.split('/').pop() : token;
            return !!extension && (tokenExt === extension || (tokenExt === 'jpeg' && extension === 'jpg'));
        });
    }

    public getAcceptableFileTypes(acceptedType: string) {
        switch (acceptedType) {
            case 'image':
                return this.uploadLimits.image_types;
            case 'video':
                return this.uploadLimits.video_types;
            case 'document':
                return this.uploadLimits.document_types;
            case 'all':
                return [...this.uploadLimits.image_types, ...this.uploadLimits.video_types];
        }
    }

    private formatAcceptableFileTypes(types: string[]): string {
        const labels = [...new Set(types.map(type => this.normalizeTypeLabel(type)).filter(Boolean))];

        if (labels.length === 0) {
            return '';
        }

        if (labels.length === 1) {
            return labels[0];
        }

        if (labels.length === 2) {
            return `${labels[0]} or ${labels[1]}`;
        }

        return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
    }

    private normalizeTypeLabel(type: string): string {
        const normalized = type.toLowerCase().replace(/^\./, '');

        switch (normalized) {
            case 'jpeg':
            case 'jpg':
                return 'JPG';
            case 'png':
                return 'PNG';
            case 'mp4':
            case 'mpeg4':
                return 'MP4';
            case 'mov':
            case 'quicktime':
                return 'MOV';
            case 'mpeg':
                return 'MPEG';
            default:
                return normalized.toUpperCase();
        }
    }

    private bsModalRef: BsModalRef;
}
